/**
 * Helpers for resolving "the last thing the counterparty said" on a
 * transaction. Looks at `tx.messages[]` first; falls back to the stock
 * first-contact fields on `tx.attributes.protectedData`
 * (`customerDefaultMessage`, `providerDefaultMessage`, `inquiryMessage`,
 * `initialInquiry`).
 */

type Role = 'customer' | 'provider';

type Sender = { id?: { uuid?: string } };

type Message = {
  attributes?: { content?: string; createdAt?: string | Date };
  sender?: Sender;
  // Some call sites fetch messages via sdk.messages.query and have the
  // sender id under relationships.sender.data instead of a denormalized
  // `sender` entity.
  relationships?: { sender?: { data?: { id?: { uuid?: string } } } };
};

type ProtectedData = {
  customerDefaultMessage?: unknown;
  providerDefaultMessage?: unknown;
  inquiryMessage?: unknown;
  initialInquiry?: unknown;
};

type Transaction = {
  messages?: Message[];
  attributes?: { protectedData?: ProtectedData | null };
};

export type LastTransactionMessage = {
  /** Raw string content. */
  content: string;
  /** True when the content came from a stock first-contact protectedData field rather than a real thread message. */
  isFirstContact: boolean;
  /** True when the most recent message was authored by the viewer themselves (only set on the own-message fallback). */
  isOwn?: boolean;
};

const trimmedString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const senderIdOf = (msg: Message): string | null =>
  msg?.sender?.id?.uuid ?? msg?.relationships?.sender?.data?.id?.uuid ?? null;

/**
 * Lower-level entry point. Resolves the last counterparty message given
 * the parts separately, useful when messages were fetched via
 * `sdk.messages.query` rather than included on the transaction.
 */
export const getLastCounterpartyMessageFromParts = ({
  messages,
  protectedData,
  viewerRole,
  otherUserId,
}: {
  messages: Message[] | null | undefined;
  protectedData: ProtectedData | null | undefined;
  viewerRole: Role;
  otherUserId: string | null | undefined;
}): LastTransactionMessage | null => {
  const thread = Array.isArray(messages) ? messages : [];

  // Walk the thread newest-first for the latest counterparty message.
  for (let i = thread.length - 1; i >= 0; i -= 1) {
    const message = thread[i];
    const senderId = senderIdOf(message);
    if (!senderId || !otherUserId || senderId !== otherUserId) continue;
    const content = trimmedString(message?.attributes?.content);
    if (content) return { content, isFirstContact: false };
  }

  // Stock first-contact field on protectedData.
  const pd = protectedData || {};
  const isViewerCustomer = viewerRole === 'customer';
  const firstContact = isViewerCustomer
    ? trimmedString(pd.providerDefaultMessage)
    : trimmedString(pd.customerDefaultMessage) ||
      trimmedString(pd.inquiryMessage) ||
      trimmedString(pd.initialInquiry);
  if (firstContact) return { content: firstContact, isFirstContact: true };

  // Final fallback: latest message from anyone (typically the viewer's own
  // outbound). Surfaces a preview when the counterparty hasn't replied yet
  // and the only protectedData first-contact is the viewer's own.
  for (let i = thread.length - 1; i >= 0; i -= 1) {
    const message = thread[i];
    const content = trimmedString(message?.attributes?.content);
    if (content) return { content, isFirstContact: false, isOwn: true };
  }

  // Last-ditch: own-side first-contact field, useful for offer-pending txs
  // where the buyer's `customerDefaultMessage` is the only "message".
  const ownFirstContact = isViewerCustomer
    ? trimmedString(pd.customerDefaultMessage) ||
      trimmedString(pd.inquiryMessage) ||
      trimmedString(pd.initialInquiry)
    : trimmedString(pd.providerDefaultMessage);

  return ownFirstContact
    ? { content: ownFirstContact, isFirstContact: true, isOwn: true }
    : null;
};

/**
 * Convenience wrapper for callers that have `tx.messages` already
 * populated (i.e. the transaction was queried with
 * `include: ['messages', 'messages.sender']`).
 */
export const getLastCounterpartyMessage = (
  transaction: Transaction | null | undefined,
  viewerRole: Role,
  otherUserId: string | null | undefined
): LastTransactionMessage | null =>
  getLastCounterpartyMessageFromParts({
    messages: transaction?.messages,
    protectedData: transaction?.attributes?.protectedData ?? null,
    viewerRole,
    otherUserId,
  });

type EntityRef = { id?: { uuid?: string } } & Record<string, unknown>;

type TxTransition = { transition?: string; createdAt?: Date | string };

const CUSTOMER_NOTE_TRANSITIONS = [
  'transition/customer-make-offer',
  'transition/customer-make-counter-offer',
  'transition/inquire',
];
const PROVIDER_NOTE_TRANSITIONS = [
  'transition/make-offer-after-inquiry',
  'transition/provider-respond-to-customer-offer',
  'transition/provider-make-counter-offer',
  'transition/update-offer',
  'transition/update-from-update-pending',
];

const findTransitionTime = (transitions: TxTransition[], names: string[]): Date | null => {
  if (!Array.isArray(transitions)) return null;
  for (const name of names) {
    const hit = transitions.find(t => t?.transition === name);
    if (hit?.createdAt) return new Date(hit.createdAt);
  }
  return null;
};

/**
 * Build pseudo-messages from the chat-offer-buy first-contact protectedData
 * fields. Sender attribution is the customer/provider entity (not viewer-
 * dependent). Timestamps come from the matching transition in `transitions`
 * when supplied, otherwise fall back: customer note -> txCreatedAt,
 * provider note -> lastTransitionedAt.
 */
export const synthesizeProtectedDataMessages = ({
  protectedData,
  customer,
  provider,
  txCreatedAt,
  lastTransitionedAt,
  transitions,
}: {
  protectedData: ProtectedData | null | undefined;
  customer: EntityRef | null | undefined;
  provider: EntityRef | null | undefined;
  txCreatedAt: Date | string | null | undefined;
  lastTransitionedAt: Date | string | null | undefined;
  transitions?: TxTransition[] | null;
}) => {
  const pd = protectedData || {};
  const created = txCreatedAt ? new Date(txCreatedAt) : null;
  const lastAt = lastTransitionedAt ? new Date(lastTransitionedAt) : created;
  const txList = transitions || [];
  const customerAt = findTransitionTime(txList, CUSTOMER_NOTE_TRANSITIONS) || created;
  const providerAt = findTransitionTime(txList, PROVIDER_NOTE_TRANSITIONS) || lastAt;
  const out: Array<{
    id: { uuid: string };
    type: 'message';
    sender: EntityRef;
    attributes: { content: string; createdAt: Date | null };
  }> = [];

  const customerNote =
    trimmedString(pd.customerDefaultMessage) || trimmedString(pd.inquiryMessage);
  if (customerNote && customer?.id) {
    out.push({
      id: { uuid: 'initial-customer' },
      type: 'message',
      sender: customer,
      attributes: { content: customerNote, createdAt: customerAt },
    });
  }
  const providerNote = trimmedString(pd.providerDefaultMessage);
  if (providerNote && provider?.id) {
    out.push({
      id: { uuid: 'initial-provider' },
      type: 'message',
      sender: provider,
      attributes: { content: providerNote, createdAt: providerAt },
    });
  }
  return out;
};

/**
 * Stable ascending sort of messages by `attributes.createdAt`. Handles
 * Date / ISO-string / missing values consistently.
 */
export const sortMessagesByCreatedAtAsc = <T extends { attributes?: { createdAt?: Date | string } }>(
  messages: T[]
): T[] =>
  [...messages].sort((a, b) => {
    const aAt = a?.attributes?.createdAt ? new Date(a.attributes.createdAt).getTime() : 0;
    const bAt = b?.attributes?.createdAt ? new Date(b.attributes.createdAt).getTime() : 0;
    return aAt - bAt;
  });

/**
 * Build the chronological conversation feed for chat surfaces: synthesized
 * first-contact pseudo-messages + real `sdk.messages.send` messages, sorted
 * ascending by createdAt.
 */
export const buildConversationMessages = ({
  protectedData,
  customer,
  provider,
  txCreatedAt,
  lastTransitionedAt,
  transitions,
  realMessages,
}: {
  protectedData: ProtectedData | null | undefined;
  customer: EntityRef | null | undefined;
  provider: EntityRef | null | undefined;
  txCreatedAt: Date | string | null | undefined;
  lastTransitionedAt: Date | string | null | undefined;
  transitions?: TxTransition[] | null;
  realMessages:
    | Array<{ attributes?: { createdAt?: Date | string } }>
    | null
    | undefined;
}) =>
  sortMessagesByCreatedAtAsc([
    ...synthesizeProtectedDataMessages({
      protectedData,
      customer,
      provider,
      txCreatedAt,
      lastTransitionedAt,
      transitions,
    }),
    ...(realMessages || []),
  ]);
